// ABOUTME: App-level subscription to /events/stream. Every delivered
// Notification refreshes the bell + jobs store and, for entity_type
// `agent_approval`, invalidates the TanStack Query caches Today/Approvals
// read from. Every delivered AgentEvent invalidates the matching
// `['domain', entity]` query key generically, so a new emit_domain_event()
// call site needs no frontend change to start driving a live update — just
// a query registered under that key. Reconnects with a small backoff when
// the stream drops (exponential backoff with jitter, reset once a connection
// stayed up for 30 s); the shell's 60s safety refetch covers any gap.

import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { subscribeEventStream } from '@/api/eventStream'
import { tokenManager } from '@/api/client'
import { useNotificationsStore } from '@/stores/useNotificationsStore'
import { useJobsStore } from '@/stores/useJobsStore'
import { useHermesReplyStore } from '@/stores/useHermesReplyStore'
import {
  AGENT_ROLE_MESSAGES_KEY,
  agentRoleMessagesKey,
  agentRoleReplyFailureKey,
  ROSTER_STATUS_KEY,
  type AgentRoleReplyFailure,
} from '@/api/agentRoles'
import { PROSPECTING_KEY } from '@/api/prospecting'
import { AGENT_RUN_ACTIVITY_KEY } from '@/api/agentRuns'
import { OPEN_BRIEFS, PRODUCTION_BRIEFS_KEY } from '@/api/productionBriefs'

const RECONNECT_BASE_MS = 5_000
const RECONNECT_MAX_MS = 60_000
// A connection that stayed up this long counts as healthy and resets the backoff.
const HEALTHY_AFTER_MS = 30_000

// Notification types whose arrival means the approvals/Today "needs you"
// list changed — approval created, approved, rejected, or a retry outcome.
const APPROVAL_ENTITY_TYPE = 'agent_approval'

// The in-app chat reply/failure notification (#2569 Part 1) — the same
// `agent_run_completed` / `agent_run_failed` type also fires for other
// channels (WhatsApp, Slack, email) and for non-chat background runs, so only
// `payload.channel === "in_app"` is a chat reply.
const AGENT_RUN_ENTITY_TYPE = 'agent_run'

// A platform request changed state — its thread card re-reads, and a newly
// arrived request joins the thread.
const MARKETING_TASK_ENTITY_TYPE = 'marketing_task'
// Work handed to a team member: its card (and, for a new brief, the thread that now holds
// it), the agents' working rows and Today's "In progress" re-read.
const PRODUCTION_BRIEF_ENTITY_TYPE = 'production_brief'
const AGENT_REPORT_TYPE = 'agent_report'
const IN_APP_CHANNEL = 'in_app'
// A prospecting search or outreach step moved: the CRM's outreach (drafts, ranks) re-reads.
const PROSPECTING_TYPE_PREFIXES = ['prospect_', 'outreach_']
// A direct chat with one agent role: the run's reply (or failure) belongs on
// that role's thread, named by `payload.agent_role_id`.
const TEAM_ROLE_DM_CHANNEL = 'team_role_dm'

export function useEventStream(): void {
  const queryClient = useQueryClient()
  const disposedRef = useRef(false)

  useEffect(() => {
    disposedRef.current = false
    let unsubscribe: (() => void) | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let failures = 0
    let openedAt = 0

    const connect = () => {
      if (disposedRef.current) return
      if (!tokenManager.hasValidToken()) return
      unsubscribe = subscribeEventStream({
        onNotification: (notification) => {
          const briefId = notification.payload?.production_brief_id
          void useNotificationsStore.getState().fetchNotifications()
          void useJobsStore.getState().loadJobs()
          if (notification.type.startsWith('inbox_')) {
            void queryClient.invalidateQueries({ queryKey: ['inbox'] })
          }
          if (notification.entity_type === APPROVAL_ENTITY_TYPE) {
            void queryClient.invalidateQueries({ queryKey: ['agent-approvals'] })
            void queryClient.invalidateQueries({ queryKey: ['today'] })
          }
          if (PROSPECTING_TYPE_PREFIXES.some((prefix) => notification.type.startsWith(prefix))) {
            void queryClient.invalidateQueries({ queryKey: [PROSPECTING_KEY] })
          }
          if (notification.entity_type === AGENT_RUN_ENTITY_TYPE) {
            // A run that starts or ends on a brief moves its role's queue, so every brief
            // view re-reads.
            void queryClient.invalidateQueries({ queryKey: [AGENT_RUN_ACTIVITY_KEY] })
            if (typeof briefId === 'string') {
              void queryClient.invalidateQueries({ queryKey: [PRODUCTION_BRIEFS_KEY] })
              void queryClient.invalidateQueries({ queryKey: ROSTER_STATUS_KEY })
            }
          }
          if (notification.type === AGENT_REPORT_TYPE) {
            // An agent's report from a run it was woken for lands in Marketing's thread (a team
            // member's post on its brief too, which its own chat also shows) or a member's own.
            void queryClient.invalidateQueries({ queryKey: ['domain', 'conversation_message'] })
            void queryClient.invalidateQueries({ queryKey: [AGENT_ROLE_MESSAGES_KEY] })
          }
          if (notification.entity_type === PRODUCTION_BRIEF_ENTITY_TYPE) {
            void queryClient.invalidateQueries({ queryKey: [PRODUCTION_BRIEFS_KEY] })
            void queryClient.invalidateQueries({ queryKey: ROSTER_STATUS_KEY })
            void queryClient.invalidateQueries({ queryKey: ['domain', 'conversation_message'] })
          }
          if (notification.entity_type === MARKETING_TASK_ENTITY_TYPE) {
            void queryClient.invalidateQueries({ queryKey: ['marketing-task'] })
            void queryClient.invalidateQueries({ queryKey: ['domain', 'conversation_message'] })
          }
          if (
            notification.entity_type === AGENT_RUN_ENTITY_TYPE &&
            notification.payload?.channel === IN_APP_CHANNEL
          ) {
            const answers = notification.payload?.answers as string[] | undefined
            if (notification.type === 'agent_run_completed') {
              useHermesReplyStore.getState().publish({
                notificationId: notification.id,
                kind: 'reply',
                messageId: notification.payload?.message_id as string | undefined,
                content: notification.payload?.reply_text as string | undefined,
                createdAt: notification.payload?.created_at as string | undefined,
                answers,
              })
            } else if (notification.type === 'agent_run_waiting') {
              useHermesReplyStore.getState().publish({
                notificationId: notification.id,
                kind: 'waiting',
                etaSeconds: notification.payload?.eta_seconds as number | undefined,
                answers,
              })
            } else if (
              notification.type === 'agent_run_failed' ||
              notification.type === 'agent_run_cancelled'
            ) {
              useHermesReplyStore.getState().publish({
                notificationId: notification.id,
                kind: 'failure',
                error: notification.payload?.error as string | undefined,
                answers,
              })
            }
          }
          const roleId = notification.payload?.agent_role_id
          if (
            notification.entity_type === AGENT_RUN_ENTITY_TYPE &&
            notification.payload?.channel === TEAM_ROLE_DM_CHANNEL &&
            typeof roleId === 'string'
          ) {
            if (notification.type === 'agent_run_failed') {
              queryClient.setQueryData<AgentRoleReplyFailure>(agentRoleReplyFailureKey(roleId), {
                error: (notification.payload?.error as string | undefined) ?? null,
                at: notification.created_at,
              })
            }
            void queryClient.invalidateQueries({ queryKey: agentRoleMessagesKey(roleId) })
          }
        },
        onRunStep: (step) => {
          // A step changes its run's activity row, the brief it works on and the open-brief
          // list (Today's "In progress", the agents' rows), never the other briefs' cards: a
          // refetch of every card per step is a burst the per-user rate limit refuses.
          void queryClient.invalidateQueries({ queryKey: [AGENT_RUN_ACTIVITY_KEY] })
          if (step.production_brief_id) {
            void queryClient.invalidateQueries({
              queryKey: [PRODUCTION_BRIEFS_KEY, step.production_brief_id],
            })
            void queryClient.invalidateQueries({ queryKey: [PRODUCTION_BRIEFS_KEY, OPEN_BRIEFS] })
          }
        },
        onDomainEvent: (event) => {
          void queryClient.invalidateQueries({ queryKey: ['domain', event.entity] })
        },
        onOpen: () => {
          openedAt = Date.now()
        },
        onError: () => {
          // Stream dropped — retry with exponential backoff and jitter so a
          // struggling server (or a 429) is never hammered. The 60s shell
          // refetch covers the gap.
          if (disposedRef.current) return
          if (openedAt && Date.now() - openedAt >= HEALTHY_AFTER_MS) failures = 0
          openedAt = 0
          const base = Math.min(RECONNECT_BASE_MS * 2 ** failures, RECONNECT_MAX_MS)
          failures += 1
          reconnectTimer = setTimeout(connect, base * (0.75 + Math.random() * 0.5))
        },
      })
    }

    connect()

    return () => {
      disposedRef.current = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      unsubscribe?.()
    }
  }, [queryClient])
}
